import { Microfrontend } from '@cian/mf-react';
import * as React from 'react';
import { useSelector } from 'react-redux';
import {
  InfrastructureError,
  InfrastructureLoader,
  InfrastructureWrapper,
} from 'shared/components/InfrastructureMicrofrontend';
import { YANDEX_API_KEY_CONFIG_KEY, YANDEX_SUGGEST_API_KEY_CONFIG_KEY } from 'shared/constants/map';
import { useApplicationContext } from 'shared/containers/ApplicationContext';
import { selectIsInfrastructureMicrofrontendEnabled } from 'shared/selectors/infrastructure';

import { useInfrastructureMicrofrontend } from './useInfrastructureMicrofrontend';

interface IInfrastureMicrofrontendContainerProps {
  headerLinks?: React.ReactNode;
  wrapped?: boolean;
  profile?: EInfrastructureProfile;
}

/**
 * Ключ профиля инфраструктуры,
 * в зависимости от которого будет отображаться та или иная инфраструктура на карте
 * Задается в админке
 * @see https://jira.cian.ru/browse/WEB-4259
 */
export enum EInfrastructureProfile {
  /** ключ для новостроек */
  Newbuilding = 'newbuilding',
  /** ключ для загородки */
  Suburban = 'suburban',
  /** ключ для посутки */
  DailyRent = 'daily',
  /** ключ для вторички */
  Urban = 'secondary',
}

/**
 * @todo разделить ключи для разных профилей, чтобы было проще вести учет использования yandex api
 */
const YANDEX_API_CONFIG_KEYS_MAP: Record<EInfrastructureProfile, string> = {
  [EInfrastructureProfile.Newbuilding]: YANDEX_API_KEY_CONFIG_KEY,
  [EInfrastructureProfile.DailyRent]: YANDEX_API_KEY_CONFIG_KEY,
  [EInfrastructureProfile.Suburban]: YANDEX_API_KEY_CONFIG_KEY,
  [EInfrastructureProfile.Urban]: YANDEX_API_KEY_CONFIG_KEY,
};

/**
 * @todo разделить ключи для разных профилей, чтобы было проще вести учет использования yandex api
 */
const YANDEX_SUGGEST_API_CONFIG_KEYS_MAP: Record<EInfrastructureProfile, string> = {
  [EInfrastructureProfile.Newbuilding]: YANDEX_SUGGEST_API_KEY_CONFIG_KEY,
  [EInfrastructureProfile.DailyRent]: YANDEX_SUGGEST_API_KEY_CONFIG_KEY,
  [EInfrastructureProfile.Suburban]: YANDEX_SUGGEST_API_KEY_CONFIG_KEY,
  [EInfrastructureProfile.Urban]: YANDEX_SUGGEST_API_KEY_CONFIG_KEY,
};

/**
 * @todo фолбэчиться на аудиторный ключ
 */
const FALLBACK_YANDEX_API_CONFIG_KEY = YANDEX_API_KEY_CONFIG_KEY;
const FALLBACK_YANDEX_SUGGEST_API_CONFIG_KEY = YANDEX_SUGGEST_API_KEY_CONFIG_KEY;

export function InfrastructureMicrofrontendContainer({
  headerLinks,
  wrapped,
  profile,
}: IInfrastureMicrofrontendContainerProps) {
  const { config } = useApplicationContext();
  const { manifest, fetchManifest, status } = useInfrastructureMicrofrontend();
  const isInfrastructureMicrofrontendEnabled = useSelector(selectIsInfrastructureMicrofrontendEnabled);

  const yandexApiKey = React.useMemo(() => {
    const configKey = profile ? YANDEX_API_CONFIG_KEYS_MAP[profile] : FALLBACK_YANDEX_API_CONFIG_KEY;

    return config.get<string>(configKey);
  }, [profile, config]);

  const yandexSuggestApiKey = React.useMemo(() => {
    const configKey = profile ? YANDEX_SUGGEST_API_CONFIG_KEYS_MAP[profile] : FALLBACK_YANDEX_SUGGEST_API_CONFIG_KEY;

    return config.get<string>(configKey);
  }, [profile, config]);

  const microfrontendParams = React.useMemo(
    () => ({
      ...(yandexApiKey ? { yandexApiKey } : {}),
      ...(yandexSuggestApiKey ? { yandexSuggestApiKey } : {}),
      ...(profile ? { profile } : {}),
    }),
    [yandexApiKey, yandexSuggestApiKey, profile],
  );

  if (!isInfrastructureMicrofrontendEnabled) {
    return null;
  }

  return (
    <InfrastructureWrapper wrapped={wrapped} headerLinks={headerLinks}>
      {status === 'loading' && <InfrastructureLoader />}
      {status === 'error' && <InfrastructureError retry={fetchManifest} />}
      {status === 'success' && manifest && (
        <Microfrontend
          name={manifest.microfrontendName}
          image={manifest.imageVersion}
          runtime={manifest.runtimeName}
          parameters={microfrontendParams}
        />
      )}
    </InfrastructureWrapper>
  );
}
